import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility } from 'react-icons/md';
import '../master/MasterData.css';

interface Vendor {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface OrderItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface PurchaseOrder {
  orderId: number;
  orderNo: string;
  orderDate: string;
  vendorCode: string;
  vendorName: string;
  deliveryDate: string;
  deliveryAddress: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'IN_PRODUCTION' | 'RECEIVED' | 'CANCELLED';
  items: OrderItem[];
  terms: string;
  remarks: string;
}

const PurchaseOrders: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch vendors from API
    setVendors([
      {
        vendorId: 1,
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        vendorType: 'MANUFACTURER',
        tin: '123-456-789-000',
        email: 'foam@suppliers.com',
        phone: '+63 2 1111 2222',
        city: 'Laguna',
        isActive: 'Y'
      },
      {
        vendorId: 2,
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        vendorType: 'DISTRIBUTOR',
        tin: '987-654-321-000',
        email: 'chem@trading.com',
        phone: '+63 2 3333 4444',
        city: 'Batangas',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch purchase orders from API
    setOrders([
      {
        orderId: 1,
        orderNo: 'PO-2025-0001',
        orderDate: '2025-02-01',
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        deliveryDate: '2025-02-15',
        deliveryAddress: 'Warehouse A, Laguna',
        subtotal: 892857.14,
        vatAmount: 107142.86,
        totalAmount: 1000000,
        status: 'APPROVED',
        items: [],
        terms: 'Net 30',
        remarks: 'Bulk order for Q1 production'
      },
      {
        orderId: 2,
        orderNo: 'PO-2025-0002',
        orderDate: '2025-02-10',
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        deliveryDate: '2025-02-20',
        deliveryAddress: 'Factory - Main Plant',
        subtotal: 446428.57,
        vatAmount: 53571.43,
        totalAmount: 500000,
        status: 'SUBMITTED',
        items: [],
        terms: 'Net 45',
        remarks: ''
      }
    ]);
  }, []);

  const filteredOrders = orders.filter(order =>
    order.orderNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingOrder(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (order: PurchaseOrder) => {
    setEditingOrder(order);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (order: PurchaseOrder) => {
    setEditingOrder(order);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (orderId: number) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      setOrders(orders.filter(o => o.orderId !== orderId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'badge-success';
      case 'SUBMITTED': return 'badge-info';
      case 'IN_PRODUCTION': return 'badge-primary';
      case 'RECEIVED': return 'badge-green';
      case 'CANCELLED': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Purchase Orders</h2>
            <p className="subtitle">Manage purchase orders and vendor procurement</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Order
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search purchase orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>PO No</th>
                  <th>PO Date</th>
                  <th>Vendor</th>
                  <th>Delivery Date</th>
                  <th>Subtotal</th>
                  <th>VAT (12%)</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.orderId}>
                    <td className="code">{order.orderNo}</td>
                    <td>{new Date(order.orderDate).toLocaleDateString('en-US')}</td>
                    <td>{order.vendorName}</td>
                    <td>{new Date(order.deliveryDate).toLocaleDateString('en-US')}</td>
                    <td className="amount">₱{order.subtotal.toLocaleString()}</td>
                    <td className="amount">₱{order.vatAmount.toLocaleString()}</td>
                    <td className="amount">₱{order.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(order)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(order)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(order.orderId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="no-data">
                <p>No purchase orders found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <OrderModal
          order={editingOrder}
          viewMode={viewMode}
          vendors={vendors}
          onClose={() => setShowModal(false)}
          onSave={(order) => {
            if (editingOrder) {
              setOrders(orders.map(o =>
                o.orderId === order.orderId ? order : o
              ));
            } else {
              setOrders([...orders, { ...order, orderId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface OrderModalProps {
  order: PurchaseOrder | null;
  viewMode: boolean;
  vendors: Vendor[];
  onClose: () => void;
  onSave: (order: PurchaseOrder) => void;
}

const OrderModal: React.FC<OrderModalProps> = ({ order, viewMode, vendors, onClose, onSave }) => {
  const VAT_RATE = 0.12;

  const [formData, setFormData] = useState<PurchaseOrder>(
    order || {
      orderId: 0,
      orderNo: '',
      orderDate: new Date().toISOString().split('T')[0],
      vendorCode: '',
      vendorName: '',
      deliveryDate: '',
      deliveryAddress: '',
      subtotal: 0,
      vatAmount: 0,
      totalAmount: 0,
      status: 'DRAFT',
      items: [],
      terms: 'Net 30',
      remarks: ''
    }
  );

  const handleVendorChange = (vendorCode: string) => {
    const selectedVendor = vendors.find(v => v.vendorCode === vendorCode);
    if (selectedVendor) {
      setFormData({
        ...formData,
        vendorCode: selectedVendor.vendorCode,
        vendorName: selectedVendor.vendorName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: Date.now(),
          itemCode: '',
          itemName: '',
          quantity: 1,
          unitPrice: 0,
          amount: 0
        }
      ]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof OrderItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => {
        if (item.itemId === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    });
  };

  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.amount, 0);
    const vatAmount = subtotal * VAT_RATE;
    const totalAmount = subtotal + vatAmount;
    setFormData(prev => ({ ...prev, subtotal, vatAmount, totalAmount }));
  }, [formData.items]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Purchase Order' : order ? 'Edit Purchase Order' : 'Create New Purchase Order'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>PO No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.orderNo}
                  onChange={(e) => setFormData({ ...formData, orderNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Order Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.orderDate}
                  onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Delivery Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Vendor Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.vendorCode}
                  onChange={(e) => handleVendorChange(e.target.value)}
                >
                  <option value="">Select Vendor</option>
                  {vendors.filter(v => v.isActive === 'Y').map(vendor => (
                    <option key={vendor.vendorId} value={vendor.vendorCode}>
                      {vendor.vendorName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Vendor Code *</label>
                <input
                  type="text"
                  required
                  disabled
                  value={formData.vendorCode}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="APPROVED">Approved</option>
                  <option value="IN_PRODUCTION">In Production</option>
                  <option value="RECEIVED">Received</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Delivery Address *</label>
                <textarea
                  required
                  disabled={viewMode}
                  value={formData.deliveryAddress}
                  onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Payment Terms *</label>
                <select
                  disabled={viewMode}
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                >
                  <option value="Net 7">Net 7 days</option>
                  <option value="Net 15">Net 15 days</option>
                  <option value="Net 30">Net 30 days</option>
                  <option value="Net 45">Net 45 days</option>
                  <option value="Net 60">Net 60 days</option>
                  <option value="COD">Cash on Delivery</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Order Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.itemId, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Subtotal:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.subtotal.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>VAT (12%):</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.vatAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>Total Amount:</td>
                    <td style={{ fontWeight: 'bold', fontSize: '16px' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {order ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseOrders;
